import { useState, useCallback } from 'react';
import axios from 'axios';
import { AlertMessage } from './useAlert';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface UsePrivacyToggleProps {
  itemId: string | null;
  currentPrivacyState: boolean;
  apiEndpoint: string; // e.g., 'simulation' or 'idea'
  onSuccess?: (message: string) => void;
  onError?: (error: AlertMessage) => void;
  handleApiError?: (error: any) => boolean;
}

export const usePrivacyToggle = ({
  itemId,
  currentPrivacyState,
  apiEndpoint,
  onSuccess,
  onError,
  handleApiError
}: UsePrivacyToggleProps) => {
  const [loading, setLoading] = useState(false);

  const togglePrivacy = useCallback(async (newPrivacyState?: boolean): Promise<boolean> => {
    if (!itemId) return false;

    const targetState = newPrivacyState !== undefined ? newPrivacyState : !currentPrivacyState;

    try {
      setLoading(true);

      await axios.patch(`${API_URL}/api/${apiEndpoint}/${itemId}/privacy`,
        { isPrivate: targetState },
        {
          withCredentials: true
        }
      );
      
      const successMessage = `${apiEndpoint.charAt(0).toUpperCase() + apiEndpoint.slice(1)} is now ${targetState ? 'private' : 'public'}`;
      
      if (onSuccess) {
        onSuccess(successMessage);
      }
      
      return true;
    } catch (error) {
      // Use custom error handler if provided, otherwise use generic error handling
      if (handleApiError && handleApiError(error)) {
        return false;
      }
      
      if (onError) {
        onError({
          type: 'error',
          message: 'Failed to update privacy setting'
        });
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  }, [itemId, currentPrivacyState, apiEndpoint, onSuccess, onError, handleApiError]);

  return {
    loading,
    togglePrivacy
  };
}; 